/**
 * State property boundary layers, click to identify, Pin property, Ctrl+click combined pins and the NT draw-to-pin
 * (Diurnal parity B3: 04-021, 04-022, 05-020; docs/specs/map.md §27).
 *
 * MB's decision 619 (Q544): replicate the Diurnal tool, the same states and services called from each laptop; owner
 * and tenure details for staff only, as in the Diurnal tool. The services, layers, opacities and identify rules are
 * the tool's (`aircraft-map.js` CONFIG 345-460, `setupPropertyClickHandler` 25319-25578, `tryParseFeatureInfo`,
 * `parseEsriIdentify`, `buildPropertyPopup` 25580-25960, NT tiles `src-tauri/src/tile_cache.rs` 247-290). Teslyn
 * Field fetches the tiles through its tile cache (`teslyn_core::map::property`) and every identify answer through
 * the core (`property_fetch`), so no service needs to allow the app's origin (no CORS), and nothing goes through a
 * local proxy as in the tool.
 *
 * What differs from the tool, on purpose (each a fault of the tool, docs/specs/map.md §27.6):
 * - The QLD service now names its fields by alias ("Property name", "Lot", "Plan"): the tool's lower-case lookups
 *   missed them and pinned the lot rather than the property. Both are read here, so the Properties polygon wins as
 *   the tool intends.
 * - ArcGIS rings are sorted into outer rings and holes by their direction (the tool made the first ring the outer
 *   and every other ring a hole, so a property of two parts became one part with a "hole" outside it).
 * - The OpenStreetMap query asks `out geom;` (the tool's `out geom tags;` returns no shapes, so its layer drew
 *   nothing; `tests/fixtures/property/2026-10-27/osm-humula-out-geom-tags.json`), and a relation's member ways are
 *   joined into closed rings.
 *
 * Text from a service is data, never markup: every value reaches the screen as text (`IdentifyRow`).
 */
import { isClientOnly, type TenantRole } from './hazards.js';
import type { GeoBounds } from './map-geo.js';
import type { IdentifyRow, KmlGeometry, LandParcel } from './map-kml.js';
import { kmlGeometryToMap, type MapColour, type MapFeature, type MapStroke } from './map-layers.js';
import type { LatLng } from './planning.js';

// ------------------------------------------------------------------------------------------------ the layers

export type PropertyLayerId = 'qld' | 'nsw' | 'sa' | 'wa_pastoral' | 'wa_cadastre' | 'vic' | 'tas' | 'nt';

/** The tile provider of a property layer in the tile cache (`teslyn_core::map::property`). */
export type PropertyTileProvider =
  | 'prop-qld'
  | 'prop-nsw'
  | 'prop-sa'
  | 'prop-wa-pastoral'
  | 'prop-wa-cadastre'
  | 'prop-vic'
  | 'prop-tas'
  | 'prop-nt';

export interface PropertyLayerDef {
  readonly id: PropertyLayerId;
  /** The tool's name for the layer. */
  readonly label: string;
  /** `wms` (WMS tiles; identify by ArcGIS REST where the service is ArcGIS, else WMS GetFeatureInfo),
   * `esri-dynamic` (ArcGIS MapServer export; ArcGIS identify), `nt` (NRMaps; no public identify: Draw and Pin). */
  readonly kind: 'wms' | 'esri-dynamic' | 'nt';
  readonly provider: PropertyTileProvider;
  /** The WMS endpoint, the MapServer, or NRMaps. */
  readonly url: string;
  /** WMS layer names, or the MapServer's layer ids. */
  readonly layers: string;
  readonly queryLayers?: string;
  /** ArcGIS REST identify for a WMS service whose WMS names differ from its REST layer ids (WA). */
  readonly restIdentifyUrl?: string;
  readonly restQueryLayers?: readonly number[];
  readonly infoFormats?: readonly string[];
  /** Asked after the others (WA cadastre: no attributes, so a pastoral station answers first). */
  readonly identifyPriority: number;
  /** The tool's opacity when switched on. */
  readonly opacity: number;
  readonly attribution: string;
  /** Shown under the layer's tick box. */
  readonly scaleNote?: string;
}

const WA_SLIP =
  'https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/Property_and_Planning/MapServer/WMSServer';
const WA_SLIP_REST =
  'https://services.slip.wa.gov.au/public/rest/services/SLIP_Public_Services/Property_and_Planning/MapServer';

/** The tool's eight property layers, in its order (am:345-460). */
export const PROPERTY_LAYERS: readonly PropertyLayerDef[] = [
  {
    id: 'qld',
    label: 'QLD Properties & Cadastre',
    kind: 'wms',
    provider: 'prop-qld',
    url: 'https://spatial-gis.information.qld.gov.au/arcgis/services/PlanningCadastre/LandParcelPropertyFramework/MapServer/WMSServer',
    layers: 'Properties,Cadastral_parcels13960',
    queryLayers: 'Properties',
    identifyPriority: 0,
    opacity: 0.7,
    attribution: '© State of Queensland (DNRME)',
  },
  {
    id: 'nsw',
    label: 'NSW Cadastre',
    kind: 'wms',
    provider: 'prop-nsw',
    url: 'https://maps.six.nsw.gov.au/arcgis/services/public/NSW_Cadastre/MapServer/WMSServer',
    layers: '1',
    identifyPriority: 0,
    opacity: 0.7,
    attribution: '© NSW Spatial Services',
  },
  {
    id: 'sa',
    label: 'SA Pastoral Stations & Cadastre',
    kind: 'esri-dynamic',
    provider: 'prop-sa',
    url: 'https://lsa4.geohub.sa.gov.au/server/rest/services/LSA/LocationSAViewerV34/MapServer',
    layers: '55,124',
    identifyPriority: 0,
    opacity: 0.7,
    attribution: '© Location SA',
  },
  {
    id: 'wa_pastoral',
    label: 'WA Pastoral Stations',
    kind: 'wms',
    provider: 'prop-wa-pastoral',
    url: WA_SLIP,
    // the WMS names differ from this service's REST layer ids (am:393)
    layers: '36',
    queryLayers: '36',
    restIdentifyUrl: WA_SLIP_REST,
    restQueryLayers: [122],
    infoFormats: ['application/geo+json', 'text/xml'],
    identifyPriority: 0,
    opacity: 0.35,
    attribution: '© Landgate WA',
    scaleNote: 'Pastoral station boundaries and names are available at wider scales.',
  },
  {
    id: 'wa_cadastre',
    label: 'WA Cadastre',
    kind: 'wms',
    provider: 'prop-wa-cadastre',
    url: WA_SLIP,
    layers: '113',
    queryLayers: '113',
    restIdentifyUrl: WA_SLIP_REST,
    restQueryLayers: [2],
    infoFormats: ['application/geo+json', 'text/xml'],
    identifyPriority: 1,
    opacity: 0.7,
    attribution: '© Landgate WA',
    scaleNote:
      'Cadastre appears at approximately 1:945,000 or closer. Public cadastre has no ownership attributes.',
  },
  {
    id: 'vic',
    label: 'VIC Property Boundaries',
    kind: 'wms',
    provider: 'prop-vic',
    url: 'https://opendata.maps.vic.gov.au/geoserver/wms',
    layers: 'open-data-platform:v_parcel_mp',
    identifyPriority: 0,
    opacity: 0.7,
    attribution: '© DEECA Victoria',
  },
  {
    id: 'tas',
    label: 'TAS Property Boundaries',
    kind: 'wms',
    provider: 'prop-tas',
    url: 'https://services.thelist.tas.gov.au/arcgis/services/Public/CadastreParcels/MapServer/WMSServer',
    layers: 'Cadastral_Parcels13224',
    identifyPriority: 0,
    opacity: 0.7,
    attribution: '© TheLIST Tasmania',
  },
  {
    id: 'nt',
    label: 'NT Properties & Cadastre',
    kind: 'nt',
    provider: 'prop-nt',
    url: 'https://nrmaps.nt.gov.au/nrmaps.html',
    // 232 outlines and parcel numbers, 234 pastoral properties, 236 labels, 243 tenure lines; 228 (fill) left out
    layers: '232,234,236,243',
    identifyPriority: 0,
    opacity: 1,
    attribution: '© NT Government (NRMaps / NTLIS)',
  },
];

export const PROPERTY_LAYER_IDS: readonly PropertyLayerId[] = PROPERTY_LAYERS.map((l) => l.id);

export const propertyLayerDef = (id: PropertyLayerId): PropertyLayerDef =>
  PROPERTY_LAYERS.find((l) => l.id === id)!;

/** NRMaps, opened from the NT card for the full parcel details (am:25515). */
export const NRMAPS_URL = 'https://nrmaps.nt.gov.au/nrmaps.html';

/** The layers switched on, in the order the tool asks them (its order, WA cadastre last: `identifyPriority`). */
export function identifyOrder(on: readonly PropertyLayerId[]): PropertyLayerDef[] {
  return PROPERTY_LAYERS.filter((l) => on.includes(l.id)).sort(
    (a, b) => a.identifyPriority - b.identifyPriority,
  );
}

/** A layer's opacity: the person's own, else the tool's for the layer. */
export function propertyOpacity(
  id: PropertyLayerId,
  chosen: Readonly<Partial<Record<PropertyLayerId, number>>>,
): number {
  const v = chosen[id];
  return typeof v === 'number' && Number.isFinite(v)
    ? Math.min(1, Math.max(0, v))
    : propertyLayerDef(id).opacity;
}

// ------------------------------------------------------------------------------------------------ requests

/** What the map shows when it is clicked: its box, its size in pixels and where the click was. */
export interface IdentifyView {
  readonly bounds: GeoBounds;
  readonly size: { readonly x: number; readonly y: number };
  readonly point: { readonly x: number; readonly y: number };
}

/** An ArcGIS identify of `at` (am:25374-25386 for a WMS layer's REST service, 25433-25445 for SA). */
export function esriIdentifyUrl(def: PropertyLayerDef, at: LatLng, view: IdentifyView): string {
  const rest =
    def.kind === 'esri-dynamic'
      ? def.url
      : (def.restIdentifyUrl ??
        def.url.replace(/\/services\//i, '/rest/services/').replace(/\/WMSServer$/i, ''));
  const b = view.bounds;
  const layers =
    def.kind === 'esri-dynamic'
      ? `visible:${def.layers}`
      : def.restQueryLayers
        ? `all:${def.restQueryLayers.join(',')}`
        : 'all';
  const params = new URLSearchParams({
    geometry: `${at.lng},${at.lat}`,
    geometryType: 'esriGeometryPoint',
    sr: '4326',
    layers,
    tolerance: '10',
    mapExtent: `${b.west},${b.south},${b.east},${b.north}`,
    imageDisplay: `${view.size.x},${view.size.y},96`,
    returnGeometry: 'true',
    f: 'json',
  });
  return `${rest}/identify?${params.toString()}`;
}

/** A WMS 1.3.0 GetFeatureInfo of the click (am:25398-25412), up to 20 features. */
export function wmsFeatureInfoUrl(def: PropertyLayerDef, view: IdentifyView, format: string): string {
  const b = view.bounds;
  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    REQUEST: 'GetFeatureInfo',
    LAYERS: def.layers,
    QUERY_LAYERS: def.queryLayers ?? def.layers,
    FEATURE_COUNT: '20',
    CRS: 'EPSG:4326',
    WIDTH: String(view.size.x),
    HEIGHT: String(view.size.y),
    BBOX: `${b.south},${b.west},${b.north},${b.east}`,
    I: String(Math.round(view.point.x)),
    J: String(Math.round(view.point.y)),
    INFO_FORMAT: format,
  });
  return `${def.url}?${params.toString()}`;
}

/** The formats asked in turn when a layer names none (am:25413). */
export const DEFAULT_INFO_FORMATS: readonly string[] = [
  'application/json',
  'application/geojson',
  'text/xml',
  'application/vnd.ogc.gml',
];

/** Is this WMS an ArcGIS service, so its REST identify (with shapes) is asked first (am:25370)? */
export const isArcgisWms = (url: string): boolean =>
  /\/arcgis\/services\/.+?\/MapServer\/WMSServer$/i.test(url);

// ------------------------------------------------------------------------------------------------ answers

/** Attributes of one identified feature, as the service named them. */
export type Attributes = Readonly<Record<string, string>>;

/** A placeholder, not a value (am:25581). */
export function isNullish(v: unknown): boolean {
  if (typeof v !== 'string') return true;
  const s = v.trim().toLowerCase();
  return s === '' || s === 'null' || s === '<null>' || s === 'n/a' || s === 'none' || s === 'undefined';
}

const lowerKeys = (props: Attributes): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) out[k.toLowerCase()] = v;
  return out;
};

/** The field names a property's name is read from, in turn (am:25589; "property name" is QLD's alias since 2026). */
const NAME_KEYS = [
  'property_name',
  'property name',
  'property',
  'feat_name',
  'alias_name',
  'name',
  'prop_name',
  'parcel_name',
  'pastoral_station',
  'station_name',
  'station name',
  'stationname',
  'shire_name',
  'segpar',
  'locality',
  'plan_label',
  'lot_plan',
];

/** A feature's property name, or '' (am:25589). */
export function extractPropertyName(props: Attributes): string {
  const lower = lowerKeys(props);
  for (const k of NAME_KEYS) {
    const v = lower[k];
    if (v !== undefined && !isNullish(v)) return v;
  }
  return '';
}

/** One identified property: its name, the layer, its attributes and (when the service gave it) its shape. */
export interface IdentifiedProperty {
  readonly layer: string;
  readonly name: string;
  readonly attributes: Attributes;
  readonly geometry: KmlGeometry | null;
}

/** An ArcGIS identify answer. */
interface EsriResult {
  readonly layerId?: number;
  readonly layerName?: string;
  readonly attributes?: Record<string, unknown>;
  readonly geometry?: { readonly rings?: ReadonlyArray<ReadonlyArray<readonly number[]>> } | null;
}

const attrs = (a: Record<string, unknown> | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(a ?? {})) if (v !== null && v !== undefined) out[k] = String(v);
  return out;
};

/** A service's own error, said in its words. */
export class PropertyServiceError extends Error {}

/**
 * An ArcGIS identify answer (am:25782): the Properties result names the property and gives its shape, the cadastral
 * result its lot, plan and tenure; else the first result with a name.
 */
export function parseEsriIdentify(
  json: unknown,
  layerName: string,
  preferredLayerIds?: readonly number[],
): IdentifiedProperty | null {
  if (typeof json !== 'object' || json === null) throw new PropertyServiceError('The service did not answer');
  const j = json as { error?: { message?: string }; results?: EsriResult[] };
  if (j.error) throw new PropertyServiceError(j.error.message || 'ArcGIS identify returned an error');
  let results = Array.isArray(j.results) ? j.results : [];
  if (preferredLayerIds) results = preferredLayerIds.flatMap((id) => results.filter((r) => r.layerId === id));
  if (results.length === 0) return null;
  const properties = results.filter((r) => (r.layerName ?? '').toLowerCase() === 'properties');
  const cadastral = results.filter((r) => /cadastral/i.test(r.layerName ?? ''));
  const prop = properties.find((r) => extractPropertyName(attrs(r.attributes)) !== '') ?? null;
  const hasLot = (r: EsriResult): boolean => {
    const l = lowerKeys(attrs(r.attributes));
    return Boolean(l.lot || l.plan || l.lotplan);
  };
  const cad = cadastral.find(hasLot) ?? cadastral[0] ?? null;
  let combined: Record<string, string>;
  let best: EsriResult;
  if (prop && cad) {
    const cadA = attrs(cad.attributes);
    const propA = attrs(prop.attributes);
    combined = { ...cadA, ...propA };
    for (const k of ['lot', 'plan', 'lotplan', 'tenure', 'lot_area', 'locality', 'shire_name', 'alias_name'])
      if (cadA[k] && !combined[k]) combined[k] = cadA[k];
    const propName = extractPropertyName(propA);
    if (propName) {
      combined.property_name = propName;
      if (cadA.feat_name && cadA.feat_name === propName) delete combined.feat_name;
    }
    best = prop;
  } else if (prop) {
    combined = attrs(prop.attributes);
    best = prop;
  } else if (cad) {
    combined = attrs(cad.attributes);
    best = cad;
  } else {
    best = results[0]!;
    const pool = preferredLayerIds ? results.filter((r) => r.layerId === best.layerId) : results;
    best = pool.find((r) => extractPropertyName(attrs(r.attributes)) !== '') ?? best;
    combined = attrs(best.attributes);
  }
  const rings = best.geometry?.rings;
  return {
    layer: layerName,
    name: extractPropertyName(combined) || 'Unknown',
    attributes: combined,
    geometry: rings && rings.length > 0 ? esriRingsGeometry(rings) : null,
  };
}

const xmlText = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&amp;/g, '&');

/**
 * A WMS GetFeatureInfo answer (am:25599): GeoJSON, Esri's `<FIELDS …/>` XML or GML; a service exception is an error.
 * Null when nothing was found.
 */
export function parseFeatureInfo(text: string, layerName: string): IdentifiedProperty | null {
  if (/^\s*</.test(text)) {
    const ex =
      /<(?:\w+:)?(?:ServiceException|ExceptionText)\b[^>]*>([\s\S]*?)<\/(?:\w+:)?(?:ServiceException|ExceptionText)>/i.exec(
        text,
      );
    if (ex) throw new PropertyServiceError(`WMS service exception: ${xmlText(ex[1]!.trim())}`);
  }
  let json: unknown = null;
  try {
    json = JSON.parse(text);
  } catch {
    json = null;
  }
  if (typeof json === 'object' && json !== null) {
    const j = json as {
      error?: { message?: string };
      features?: Array<{ properties?: Record<string, unknown>; geometry?: unknown }>;
    };
    if (j.error) throw new PropertyServiceError(j.error.message || 'WMS service returned an error');
    if (Array.isArray(j.features) && j.features.length > 0) {
      const named = j.features.find((f) => extractPropertyName(attrs(f.properties)) !== '');
      const f = named ?? j.features[0]!;
      const props = attrs(f.properties);
      return {
        layer: layerName,
        name: extractPropertyName(props) || 'Unknown',
        attributes: props,
        geometry: geoJsonGeometry(f.geometry),
      };
    }
    return null;
  }
  // Esri's XML: <FeatureInfoResponse><FIELDS attr="value" …/></FeatureInfoResponse>
  const fields = [...text.matchAll(/<(?:\w+:)?FIELDS\b([^>]*?)\/?>/g)].map((m) => {
    const props: Record<string, string> = {};
    for (const a of m[1]!.matchAll(/([\w:.-]+)\s*=\s*"([^"]*)"/g)) props[a[1]!] = xmlText(a[2]!);
    return props;
  });
  const withFields = fields.filter((p) => Object.keys(p).length > 0);
  if (withFields.length > 0) {
    const props = withFields.find((p) => extractPropertyName(p) !== '') ?? withFields[0]!;
    return {
      layer: layerName,
      name: extractPropertyName(props) || 'Unknown',
      attributes: props,
      geometry: null,
    };
  }
  // GML: the first featureMember's fields
  const member =
    /<(?:\w+:)?(?:featureMember|member)\b[^>]*>([\s\S]*?)<\/(?:\w+:)?(?:featureMember|member)>/.exec(text);
  if (member) {
    const props: Record<string, string> = {};
    const inner = /<(?:\w+:)?\w+\b[^>]*>([\s\S]*)<\/(?:\w+:)?\w+>/.exec(member[1]!.trim());
    for (const f of (inner?.[1] ?? '').matchAll(/<(?:\w+:)?(\w+)\b[^>]*>([^<]+)<\/(?:\w+:)?\1>/g))
      props[f[1]!] = xmlText(f[2]!);
    if (Object.keys(props).length === 0) return null;
    return {
      layer: layerName,
      name: extractPropertyName(props) || 'Unknown',
      attributes: props,
      geometry: null,
    };
  }
  return null;
}

// ------------------------------------------------------------------------------------------------ shapes

const latLng = (c: readonly number[]): LatLng => ({ lat: c[1]!, lng: c[0]! });

/** Twice the signed area (x right, y up): positive when the ring runs anticlockwise. */
function signedArea2(ring: readonly LatLng[]): number {
  let s = 0;
  for (let i = 0; i + 1 < ring.length; i += 1)
    s += ring[i]!.lng * ring[i + 1]!.lat - ring[i + 1]!.lng * ring[i]!.lat;
  return s;
}

function inside(p: LatLng, ring: readonly LatLng[]): boolean {
  let c = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const a = ring[i]!;
    const b = ring[j]!;
    if (
      a.lat > p.lat !== b.lat > p.lat &&
      p.lng < ((b.lng - a.lng) * (p.lat - a.lat)) / (b.lat - a.lat) + a.lng
    )
      c = !c;
  }
  return c;
}

/** Outer rings with their holes as a polygon, or a multi-polygon of several outer rings. */
function polygonsOf(outers: readonly LatLng[][], holes: readonly LatLng[][]): KmlGeometry | null {
  if (outers.length === 0) return null;
  const parts = outers.map((outer) => ({ outer, holes: [] as LatLng[][] }));
  for (const h of holes) {
    const p = h[0] ? parts.find((x) => inside(h[0]!, x.outer)) : undefined;
    (p ?? parts[0]!).holes.push(h);
  }
  const polys: KmlGeometry[] = parts.map((p) => ({ type: 'polygon', outer: p.outer, holes: p.holes }));
  return polys.length === 1 ? polys[0]! : { type: 'multi', parts: polys };
}

/** ArcGIS rings (x, y in degrees): clockwise rings are outer, anticlockwise ones holes. */
export function esriRingsGeometry(
  rings: ReadonlyArray<ReadonlyArray<readonly number[]>>,
): KmlGeometry | null {
  const all = rings.map((r) => r.map(latLng)).filter((r) => r.length >= 4);
  let outers = all.filter((r) => signedArea2(r) < 0);
  let holes = all.filter((r) => signedArea2(r) >= 0);
  if (outers.length === 0) {
    outers = all;
    holes = [];
  }
  return polygonsOf(outers, holes);
}

/** A GeoJSON Polygon or MultiPolygon (lng, lat), else null. */
export function geoJsonGeometry(g: unknown): KmlGeometry | null {
  if (typeof g !== 'object' || g === null) return null;
  const x = g as { type?: string; coordinates?: unknown };
  const ring = (r: unknown): LatLng[] => (Array.isArray(r) ? (r as number[][]).map(latLng) : []);
  const poly = (rs: unknown): KmlGeometry | null => {
    if (!Array.isArray(rs) || rs.length === 0) return null;
    const [outer, ...holes] = rs.map(ring);
    return outer && outer.length >= 4
      ? { type: 'polygon', outer, holes: holes.filter((h) => h.length >= 4) }
      : null;
  };
  if (x.type === 'Polygon') return poly(x.coordinates);
  if (x.type === 'MultiPolygon' && Array.isArray(x.coordinates)) {
    const parts = x.coordinates.map(poly).filter((p): p is KmlGeometry => p !== null);
    if (parts.length === 0) return null;
    return parts.length === 1 ? parts[0]! : { type: 'multi', parts };
  }
  return null;
}

/** The polygons of a shape (a multi-polygon's parts), else none. */
export function polygonParts(g: KmlGeometry | null): Array<Extract<KmlGeometry, { type: 'polygon' }>> {
  if (!g) return [];
  if (g.type === 'polygon') return [g];
  if (g.type === 'multi') return g.parts.flatMap(polygonParts);
  return [];
}

/** Has this shape an area (a polygon or a multi-polygon)? */
export const isAreaGeometry = (g: KmlGeometry | null): boolean => polygonParts(g).length > 0;

// ------------------------------------------------------------------------------------------------ the card

/** Owner, phone, address and tenure: staff only (decision 619, as the tool). */
export const canSeeOwnerDetails = (roles: readonly TenantRole[]): boolean => !isClientOnly(roles);

/** The identify card of a property: the layer as its title, the main rows, and every attribute (staff only). */
export interface PropertyCard {
  readonly title: string;
  readonly rows: readonly IdentifyRow[];
  /** "All attributes" (the tool's collapsed list); empty for a client. */
  readonly attributes: readonly IdentifyRow[];
}

/** Area fields in hectares, and in square metres (shown in hectares). Other area fields name no unit: not shown. */
const AREA_HA_KEYS = ['area_ha'];
const AREA_M2_KEYS = ['dimension_m2', 'dimension (m²)', 'lot area (m²)'];

/**
 * The identify card (`buildPropertyPopup`, am:25864): property, locality, lot and plan, owner, address, tenure, area
 * and phone, then every attribute. Owner, address, tenure, phone and the attribute list (which may hold them) are
 * for staff only (decision 619). The area is shown only from a field whose unit is known (the tool wrote "ha" after
 * any area field, square metres included).
 */
export function propertyCard(p: IdentifiedProperty, roles: readonly TenantRole[]): PropertyCard {
  const lower = lowerKeys(p.attributes);
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = lower[k];
      if (v !== undefined && !isNullish(v)) return String(v);
    }
    return '';
  };
  const staff = canSeeOwnerDetails(roles);
  const name = extractPropertyName(p.attributes);
  const lot = pick('lot', 'lotnumber', 'lot_number', 'lotno');
  const plan = pick('plan', 'plannumber', 'plan_number', 'planno');
  const phone = pick('phone', 'phone_number', 'telephone', 'contact');
  const owner = pick('owner', 'owner_name', 'ownername', 'occupier');
  const address = pick('address', 'street_address', 'loc_desc');
  const tenure = pick('tenure', 'tenure_type', 'tenuretype', 'tenure type');
  const locality = pick('shire_name', 'locality', 'segpar');
  const ha = Number.parseFloat(pick(...AREA_HA_KEYS));
  const m2 = Number.parseFloat(pick(...AREA_M2_KEYS));
  const area = Number.isFinite(ha) ? ha : Number.isFinite(m2) ? m2 / 10_000 : null;
  const rows: IdentifyRow[] = [];
  if (name) rows.push({ label: 'Property', value: name });
  if (locality && locality !== name) rows.push({ label: 'Locality', value: locality });
  if (lot || plan) rows.push({ label: 'Lot/Plan', value: `${lot}${lot && plan ? ' / ' : ''}${plan}` });
  if (staff && owner) rows.push({ label: 'Owner', value: owner });
  if (staff && address) rows.push({ label: 'Address', value: address });
  if (staff && tenure) rows.push({ label: 'Tenure', value: tenure });
  if (area !== null)
    rows.push({ label: 'Area', value: `${area.toLocaleString('en-AU', { maximumFractionDigits: 2 })} ha` });
  if (staff && phone) rows.push({ label: 'Phone', value: phone });
  const attributes = staff
    ? Object.entries(p.attributes)
        .filter(([, v]) => v.trim() !== '' && !isNullish(v))
        .map(([k, v]) => ({ label: k, value: v }))
    : [];
  return { title: p.layer, rows, attributes };
}

// ------------------------------------------------------------------------------------------------ identify

/** A service's answer as the core fetched it. */
export interface FetchedText {
  readonly status: number;
  readonly text: string;
}

export type PropertyFetch = (url: string) => Promise<FetchedText>;

/** The NT card's suggested name for a drawn property (am:25496). */
export const ntSuggestedName = (at: LatLng): string =>
  `NT Property @ ${at.lat.toFixed(4)}, ${at.lng.toFixed(4)}`;

export type IdentifyResult =
  | { readonly kind: 'found'; readonly property: IdentifiedProperty; readonly def: PropertyLayerDef }
  /** NT: no public identify; the card offers Draw and Pin (am:25491). */
  | { readonly kind: 'nt'; readonly at: LatLng; readonly name: string; readonly def: PropertyLayerDef }
  | { readonly kind: 'none' }
  | { readonly kind: 'failed'; readonly layers: readonly string[] };

const ok = (r: FetchedText): boolean => r.status >= 200 && r.status < 300;

/**
 * Identify the property under a click (am:25319-25578): each layer switched on in turn, the first that finds one
 * answers. A WMS layer of an ArcGIS service (and WA) is asked by ArcGIS identify first, for its shape; else, or
 * when that finds nothing, by WMS GetFeatureInfo in each format in turn. A layer that cannot be asked at all is
 * named in the "lookup failed" answer.
 */
export async function identifyProperty(
  on: readonly PropertyLayerId[],
  at: LatLng,
  view: IdentifyView,
  fetch: PropertyFetch,
): Promise<IdentifyResult> {
  const failed: string[] = [];
  for (const def of identifyOrder(on)) {
    try {
      let found: IdentifiedProperty | null = null;
      if (def.kind === 'wms') {
        if (def.restIdentifyUrl || isArcgisWms(def.url)) {
          try {
            const r = await fetch(esriIdentifyUrl(def, at, view));
            if (!ok(r)) throw new PropertyServiceError(`HTTP ${r.status}`);
            found = parseEsriIdentify(JSON.parse(r.text) as unknown, def.label, def.restQueryLayers);
          } catch {
            found = null; // the WMS below
          }
        }
        if (!found) {
          let responded = false;
          for (const format of def.infoFormats ?? DEFAULT_INFO_FORMATS) {
            try {
              const r = await fetch(wmsFeatureInfoUrl(def, view, format));
              if (!ok(r)) throw new PropertyServiceError(`HTTP ${r.status}`);
              if (r.text.length < 10 || /^\s*(?:<!DOCTYPE|<html)/i.test(r.text))
                throw new PropertyServiceError('Unexpected WMS feature-info response');
              const parsed = parseFeatureInfo(r.text, def.label);
              responded = true;
              if (parsed) {
                found = parsed;
                break;
              }
            } catch {
              // the next format
            }
          }
          if (!responded) throw new PropertyServiceError('All WMS feature-info formats failed');
        }
      } else if (def.kind === 'esri-dynamic') {
        const r = await fetch(esriIdentifyUrl(def, at, view));
        if (!ok(r)) continue;
        found = parseEsriIdentify(JSON.parse(r.text) as unknown, def.label);
      } else {
        return { kind: 'nt', at, name: ntSuggestedName(at), def };
      }
      if (found) return { kind: 'found', property: found, def };
    } catch {
      failed.push(def.label);
    }
  }
  return failed.length > 0 ? { kind: 'failed', layers: failed } : { kind: 'none' };
}

/** The words of an identify that found nothing (am:25571-25577). */
export function identifyMessage(r: Extract<IdentifyResult, { kind: 'none' | 'failed' }>): string {
  return r.kind === 'failed'
    ? `Property lookup failed for ${r.layers.join(', ')}. Check your connection or the layer service and try again.`
    : 'No property boundary found at this location. Ensure a property layer is switched on and you are zoomed in enough.';
}

/** The identify highlight stays this long (am:25962). */
export const HIGHLIGHT_MS = 8000;

// ------------------------------------------------------------------------------------------------ pins

/** A property to pin: from identify, an OSM boundary, the NT drawing or a Ctrl+click selection. */
export interface PropertyPin {
  readonly name: string;
  readonly source: string;
  readonly geometry: KmlGeometry;
}

/** A pin's geometry as the tool compared it (`JSON.stringify(geometry.coordinates)`): a shape is pinned once. */
export const geometryKey = (g: KmlGeometry): string => JSON.stringify(g);

/** A pin as an identify gives it: its shape, else a point where the click was ("Pin location", am:25529). */
export function pinOf(p: IdentifiedProperty, at: LatLng): PropertyPin {
  return {
    name: p.name || 'Unknown Property',
    source: p.layer,
    geometry: p.geometry ?? { type: 'point', at },
  };
}

/** The next free `parcel-<n>` id. */
export function nextParcelId(existing: readonly { readonly id: string }[]): string {
  let n = existing.length;
  const ids = new Set(existing.map((p) => p.id));
  do n += 1;
  while (ids.has(`parcel-${n}`));
  return `parcel-${n}`;
}

/** A pinned property as a landholder parcel, or null when that shape is already pinned (am:24077). */
export function parcelOfPin(pin: PropertyPin, existing: readonly LandParcel[]): LandParcel | null {
  const key = geometryKey(pin.geometry);
  if (existing.some((p) => geometryKey(p.geometry) === key)) return null;
  return {
    id: nextParcelId(existing),
    name: pin.name,
    source: pin.source,
    folder: '',
    geometry: pin.geometry,
    notes: '',
    needs_notification: false,
    objection: false,
    contacts: [],
    attributes: [],
    visible: true,
  };
}

/** The combined name of several pins: each name once, joined with " + " (am:24222). */
export const combinedName = (names: readonly string[]): string => [...new Set(names)].join(' + ');

/** The shape of several pins combined: every polygon of each, one multi-polygon (am:24204); null when none has an area. */
export function combinedGeometry(shapes: readonly KmlGeometry[]): KmlGeometry | null {
  const polys = shapes.flatMap(polygonParts);
  if (polys.length === 0) return null;
  return polys.length === 1 ? polys[0]! : { type: 'multi', parts: polys };
}

/** The Ctrl+click selection's six colours (am:25475), as tokens, in turn. */
export const SELECTION_COLOURS = [
  'map-select-1',
  'map-select-2',
  'map-select-3',
  'map-select-4',
  'map-select-5',
  'map-select-6',
] as const;
export type SelectionColour = (typeof SELECTION_COLOURS)[number];

/** One property staged by Ctrl+click (am:25460). */
export interface StagedProperty extends PropertyPin {
  readonly colour: SelectionColour;
}

/** Stage a property: once per shape, each in the next selection colour. */
export function stageProperty(
  staged: readonly StagedProperty[],
  pin: PropertyPin,
): { readonly staged: readonly StagedProperty[]; readonly message: string; readonly added: boolean } {
  const key = geometryKey(pin.geometry);
  if (staged.some((s) => geometryKey(s.geometry) === key))
    return { staged, message: `"${pin.name}" is already in the selection`, added: false };
  const colour = SELECTION_COLOURS[staged.length % SELECTION_COLOURS.length]!;
  const next = [...staged, { ...pin, colour }];
  return { staged: next, message: `Added "${pin.name}" to the selection (${next.length})`, added: true };
}

/** "n properties selected" on the selection bar (am:25302). */
export const selectionWords = (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'} selected`;

/**
 * "Pin and combine" (am:5423): every staged property not pinned already becomes one parcel; two or more new ones are
 * combined into one "A + B" property. The result's parcels to add, and its words.
 */
export function pinStaged(
  staged: readonly PropertyPin[],
  existing: readonly LandParcel[],
): { readonly parcels: LandParcel[]; readonly message: string } {
  const fresh: PropertyPin[] = [];
  const seen = new Set(existing.map((p) => geometryKey(p.geometry)));
  for (const s of staged) {
    const k = geometryKey(s.geometry);
    if (seen.has(k)) continue;
    seen.add(k);
    fresh.push(s);
  }
  if (fresh.length === 0) return { parcels: [], message: 'Every selected property is pinned already' };
  if (fresh.length === 1) {
    const p = parcelOfPin(fresh[0]!, existing);
    return { parcels: p ? [p] : [], message: `Pinned "${fresh[0]!.name}"` };
  }
  const geometry = combinedGeometry(fresh.map((f) => f.geometry));
  if (!geometry) {
    const out: LandParcel[] = [];
    for (const f of fresh) {
      const p = parcelOfPin(f, [...existing, ...out]);
      if (p) out.push(p);
    }
    return { parcels: out, message: `Pinned ${out.length} properties (no area to combine)` };
  }
  const name = combinedName(fresh.map((f) => f.name));
  const source = [...new Set(fresh.map((f) => f.source))].join(', ');
  const p = parcelOfPin({ name, source, geometry }, existing);
  return {
    parcels: p ? [p] : [],
    message: `Combined ${fresh.length} properties into "${name}"`,
  };
}

// ------------------------------------------------------------------------------------------------ NT drawing

/** A drawn NT property's source (am:8608). */
export const NT_DRAWN_SOURCE = 'NT Cadastre (drawn)';

/** Why a drawn property's name cannot be used (required, up to 80 characters, am:8632), or null. */
export function ntNameProblem(name: string): string | null {
  const n = name.trim();
  if (!n) return 'Type a name for the property';
  if (n.length > 80) return 'Keep the name to 80 characters';
  return null;
}

/** A drawn property needs three points (am:8598). */
export const canFinishProperty = (points: readonly LatLng[]): boolean => points.length >= 3;

/** The drawn outline as a closed polygon (am:8605). */
export function drawnPropertyGeometry(points: readonly LatLng[]): KmlGeometry {
  return { type: 'polygon', outer: [...points, points[0]!], holes: [] };
}

// ------------------------------------------------------------------------------------------------ OpenStreetMap

/** The OSM boundaries need this zoom or closer (am:23656). */
export const OSM_MIN_ZOOM = 8;
/** The largest box asked, in degrees of latitude and longitude (am:23729). */
export const OSM_MAX_BOX = { lat: 8, lng: 12 } as const;
/** Asked again this long after the map stops (am:23669). */
export const OSM_DEBOUNCE_MS = 600;

/** The Overpass mirrors with worldwide data, tried in turn (as hazard maps, `OVERPASS_MIRRORS`). */
export const OVERPASS_MIRRORS: readonly string[] = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];

/** Can the boundaries be asked for this view: too far out, too wide, or yes. */
export function osmViewState(bounds: GeoBounds, zoom: number): 'too_far' | 'too_wide' | 'ok' {
  if (zoom < OSM_MIN_ZOOM) return 'too_far';
  if (bounds.north - bounds.south > OSM_MAX_BOX.lat || bounds.east - bounds.west > OSM_MAX_BOX.lng)
    return 'too_wide';
  return 'ok';
}

/** The Overpass query of the tool (am:23743), with `out geom;` so the shapes come back. */
export function osmBoundaryQuery(b: GeoBounds): string {
  const box = `${b.south},${b.west},${b.north},${b.east}`;
  return (
    `[out:json][timeout:20];(relation["boundary"~"administrative|protected_area"](${box});` +
    `way["boundary"="administrative"](${box});relation["landuse"~"farmyard|farm"](${box})["name"];` +
    `way["landuse"~"farmyard|farm"](${box})["name"];);out geom;`
  );
}

/** The address of the query on a mirror. */
export const osmBoundaryUrl = (mirror: string, b: GeoBounds): string =>
  `${mirror}?data=${encodeURIComponent(osmBoundaryQuery(b))}`;

/**
 * The box asked for a view: the view grown by half its size each way (kept within the largest box), so panning
 * inside it asks nothing again.
 */
export function osmFetchBox(view: GeoBounds): GeoBounds {
  const dLat = Math.min((view.north - view.south) / 2, (OSM_MAX_BOX.lat - (view.north - view.south)) / 2);
  const dLng = Math.min((view.east - view.west) / 2, (OSM_MAX_BOX.lng - (view.east - view.west)) / 2);
  const r = (v: number): number => Math.round(v * 1e5) / 1e5;
  return {
    south: r(Math.max(-90, view.south - Math.max(0, dLat))),
    north: r(Math.min(90, view.north + Math.max(0, dLat))),
    west: r(view.west - Math.max(0, dLng)),
    east: r(view.east + Math.max(0, dLng)),
  };
}

/** Is `inner` inside `outer`? */
export const boxWithin = (inner: GeoBounds, outer: GeoBounds): boolean =>
  inner.south >= outer.south &&
  inner.north <= outer.north &&
  inner.west >= outer.west &&
  inner.east <= outer.east;

export type OsmBoundaryKind = 'protected' | 'region' | 'local' | 'locality' | 'farm' | 'area';

/** One OpenStreetMap boundary. */
export interface OsmBoundary {
  /** `relation/5981187`. */
  readonly id: string;
  readonly name: string;
  readonly kind: OsmBoundaryKind;
  readonly admin_level: number;
  /** The kind in words: "Boundary (administrative)", "Land use: farm" or "Area". */
  readonly type_words: string;
  /** Its tags, without the source tags (am:23797). */
  readonly tags: ReadonlyArray<readonly [string, string]>;
  readonly geometry: KmlGeometry;
}

interface OsmPoint {
  readonly lat: number;
  readonly lon: number;
}
interface OsmElement {
  readonly type: string;
  readonly id: number;
  readonly tags?: Record<string, string>;
  readonly geometry?: readonly OsmPoint[];
  readonly members?: ReadonlyArray<{ readonly role?: string; readonly geometry?: readonly OsmPoint[] }>;
}

const samePoint = (a: LatLng, b: LatLng): boolean =>
  Math.abs(a.lat - b.lat) < 1e-9 && Math.abs(a.lng - b.lng) < 1e-9;

/** Member ways joined end to end into closed rings; chains that never close are left out. */
export function joinRings(ways: readonly (readonly LatLng[])[]): LatLng[][] {
  const pool = ways.filter((w) => w.length >= 2).map((w) => [...w]);
  const rings: LatLng[][] = [];
  while (pool.length > 0) {
    let chain = pool.shift()!;
    let grew = true;
    while (!samePoint(chain[0]!, chain[chain.length - 1]!) && grew) {
      grew = false;
      for (let i = 0; i < pool.length; i += 1) {
        const w = pool[i]!;
        const end = chain[chain.length - 1]!;
        const start = chain[0]!;
        if (samePoint(end, w[0]!)) chain = [...chain, ...w.slice(1)];
        else if (samePoint(end, w[w.length - 1]!)) chain = [...chain, ...[...w].reverse().slice(1)];
        else if (samePoint(start, w[w.length - 1]!)) chain = [...w.slice(0, -1), ...chain];
        else if (samePoint(start, w[0]!)) chain = [...[...w].reverse().slice(0, -1), ...chain];
        else continue;
        pool.splice(i, 1);
        grew = true;
        break;
      }
    }
    if (chain.length >= 4 && samePoint(chain[0]!, chain[chain.length - 1]!)) rings.push(chain);
  }
  return rings;
}

const pts = (g: readonly OsmPoint[] | undefined): LatLng[] =>
  (g ?? [])
    .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lon))
    .map((p) => ({ lat: p.lat, lng: p.lon }));

/** The boundaries of an Overpass answer (`osmtogeojson`, am:23683, with member ways joined into rings). */
export function osmBoundaries(json: unknown): OsmBoundary[] {
  const els = (json as { elements?: OsmElement[] } | null)?.elements;
  if (!Array.isArray(els)) return [];
  const out: OsmBoundary[] = [];
  for (const el of els) {
    const tags = el.tags ?? {};
    if (Object.keys(tags).length === 0) continue;
    let geometry: KmlGeometry | null = null;
    if (el.type === 'way') {
      const ring = pts(el.geometry);
      if (ring.length < 2) continue;
      geometry =
        ring.length >= 4 && samePoint(ring[0]!, ring[ring.length - 1]!)
          ? { type: 'polygon', outer: ring, holes: [] }
          : { type: 'line_string', points: ring };
    } else if (el.type === 'relation') {
      const members = el.members ?? [];
      const outers = joinRings(members.filter((m) => m.role !== 'inner').map((m) => pts(m.geometry)));
      const inners = joinRings(members.filter((m) => m.role === 'inner').map((m) => pts(m.geometry)));
      geometry = polygonsOf(outers, inners);
    }
    if (!geometry) continue;
    const level = Number.parseInt(tags.admin_level ?? '', 10) || 0;
    const boundary = tags.boundary ?? '';
    const landuse = tags.landuse ?? '';
    const kind: OsmBoundaryKind =
      boundary === 'protected_area'
        ? 'protected'
        : level > 0 && level <= 6
          ? 'region'
          : level >= 7 && level <= 8
            ? 'local'
            : level >= 9
              ? 'locality'
              : landuse === 'farmyard' || landuse === 'farm'
                ? 'farm'
                : 'area';
    out.push({
      id: `${el.type}/${el.id}`,
      name: tags.name ?? tags['name:en'] ?? '',
      kind,
      admin_level: level,
      type_words: boundary ? `Boundary (${boundary})` : landuse ? `Land use: ${landuse}` : 'Area',
      tags: Object.entries(tags).filter(([k]) => !['source', 'source:geometry', 'source:name'].includes(k)),
      geometry,
    });
  }
  return out;
}

/** The identify rows of an OSM boundary (am:23794). */
export function osmBoundaryRows(b: OsmBoundary): IdentifyRow[] {
  return [
    { label: 'Boundary', value: b.name || b.type_words },
    {
      label: 'Kind',
      value: `${b.type_words}${b.admin_level ? `, admin level ${b.admin_level}` : ''}, OpenStreetMap`,
    },
    ...b.tags.map(([k, v]) => ({ label: k, value: v })),
  ];
}

/** An OSM boundary as a pin ("Pin boundary", am:23802), when it is an area. */
export function osmPin(b: OsmBoundary): PropertyPin | null {
  return isAreaGeometry(b.geometry)
    ? { name: b.name || b.type_words, source: 'OSM', geometry: b.geometry }
    : null;
}

/** OpenStreetMap's credit (ODbL). */
export const OSM_CREDIT = '© OpenStreetMap contributors (ODbL)';

// ------------------------------------------------------------------------------------------------ on the map

const feature = (
  id: string,
  layer: 'osm_boundaries' | 'property_identify',
  g: KmlGeometry,
  colour: MapColour,
  stroke: MapStroke,
  dashed: boolean,
  fill: number,
  tooltip: string,
  identify: readonly IdentifyRow[],
): MapFeature[] =>
  kmlGeometryToMap(g).map((geometry, j) => ({
    id: `${id}.${j}`,
    layer,
    geometry,
    style: { colour, stroke, dashed, fill },
    tooltip,
    identify,
  }));

/** The colour, width and dash of an OSM boundary by kind (am:23763-23768). */
export const OSM_STYLE: Readonly<
  Record<OsmBoundaryKind, { colour: MapColour; stroke: MapStroke; dashed: boolean; word: string }>
> = {
  protected: { colour: 'map-osm-protected', stroke: 'thin', dashed: true, word: 'Protected area' },
  region: {
    colour: 'map-osm-region',
    stroke: 'normal',
    dashed: false,
    word: 'State or region (admin level 1 to 6)',
  },
  local: {
    colour: 'map-osm-local',
    stroke: 'thin',
    dashed: false,
    word: 'Local government (admin level 7 or 8)',
  },
  locality: {
    colour: 'map-osm-locality',
    stroke: 'thin',
    dashed: true,
    word: 'Locality (admin level 9 or more)',
  },
  farm: { colour: 'map-osm-farm', stroke: 'thin', dashed: true, word: 'Farm' },
  area: { colour: 'map-osm-local', stroke: 'thin', dashed: false, word: 'Other area' },
};

/** The OSM boundaries on the map, with a faint fill (am:23771), their name on hover. */
export function osmBoundaryFeatures(bs: readonly OsmBoundary[]): MapFeature[] {
  return bs.flatMap((b) => {
    const s = OSM_STYLE[b.kind];
    return feature(
      `osm:${b.id}`,
      'osm_boundaries',
      b.geometry,
      s.colour,
      s.stroke,
      s.dashed,
      0.04,
      b.name || b.type_words,
      osmBoundaryRows(b),
    );
  });
}

/** The identified property, outlined for 8 s (am:25953). */
export function highlightFeatures(g: KmlGeometry, name: string): MapFeature[] {
  return feature('highlight', 'property_identify', g, 'map-property-highlight', 'normal', true, 0.15, name, [
    { label: 'Identified', value: name },
  ]);
}

/** The Ctrl+click selection, each in its own colour (am:25478). */
export function stagedFeatures(staged: readonly StagedProperty[]): MapFeature[] {
  return staged.flatMap((s, i) =>
    feature(
      `staged:${i}`,
      'property_identify',
      s.geometry,
      s.colour,
      'normal',
      true,
      0.2,
      `Selected: ${s.name}`,
      [
        { label: 'Selected', value: s.name },
        { label: 'Source', value: s.source },
      ],
    ),
  );
}

/** The NT outline being drawn and its points (am:8586). */
export function ntDrawFeatures(points: readonly LatLng[]): MapFeature[] {
  const out: MapFeature[] = [];
  if (points.length >= 2)
    out.push({
      id: 'nt-draw',
      layer: 'property_identify',
      geometry: { type: 'polygon', rings: [points] },
      style: { colour: 'map-property-draw', stroke: 'normal', dashed: true, fill: 0.12 },
      tooltip: 'NT property being drawn',
      identify: [{ label: 'NT property', value: `${points.length} points` }],
    });
  points.forEach((at, i) =>
    out.push({
      id: `nt-draw:${i}`,
      layer: 'property_identify',
      geometry: { type: 'point', at },
      style: { colour: 'map-property-draw', stroke: 'normal', dashed: false, fill: 0 },
      tooltip: `Point ${i + 1}`,
      identify: [{ label: 'Point', value: `${i + 1}` }],
    }),
  );
  return out;
}
